import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { identifyUser, resetPostHog, trackOnboardingCompleted } from '@/lib/posthog';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  effectiveUser: User | null;
  signUp: (email: string, password: string, fullName: string, captchaToken?: string, desiredSpecialty?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: Error | null }>;
  signInWithDiscord: () => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string, captchaToken?: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  onboardingCompleted: boolean;
  onboardingLoading: boolean;
  completeOnboarding: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  
  const effectiveUser = user;

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        const newUser = session?.user ?? null;
        setUser(newUser);

        if (newUser) {
          identifyUser(newUser.id, newUser.email, newUser.user_metadata?.full_name);
          Sentry.setUser({
            id: newUser.id,
            email: newUser.email,
            username: newUser.user_metadata?.username,
          });
        } else {
          Sentry.setUser(null);
        }

        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      const newUser = session?.user ?? null;
      setUser(newUser);

      if (newUser) {
        identifyUser(newUser.id, newUser.email, newUser.user_metadata?.full_name);
        Sentry.setUser({
          id: newUser.id,
          email: newUser.email,
          username: newUser.user_metadata?.username,
        });
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch profile to get onboarding status
  useEffect(() => {
    if (!effectiveUser) {
      setOnboardingCompleted(false);
      setOnboardingLoading(false);
      return;
    }

    const fetchProfile = async () => {
      setOnboardingLoading(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('user_id', effectiveUser.id)
          .maybeSingle();

        if (error) throw error;
        if (data) {
          setOnboardingCompleted(data.onboarding_completed || false);
        }
      } catch (err) {
        console.error('Error fetching profile in useAuth:', err);
      } finally {
        setOnboardingLoading(false);
      }
    };

    fetchProfile();
  }, [effectiveUser]);

  const signUp = async (email: string, password: string, fullName: string, captchaToken?: string, desiredSpecialty?: string) => {
    const redirectUrl = `${window.location.origin}/app`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          ...(desiredSpecialty ? { desired_specialty: desiredSpecialty } : {}),
        },
        captchaToken,
      }
    });

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken },
    });

    return { error: error as Error | null };
  };

  const signInWithDiscord = async () => {
    const redirectUrl = `${window.location.origin}/app`;
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: { redirectTo: redirectUrl }
    });
    return { error: error as Error | null };
  };

  const signInWithGoogle = async () => {
    const redirectUrl = `${window.location.origin}/app`;
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { 
        redirectTo: redirectUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });
    return { error: error as Error | null };
  };

  const resetPassword = async (email: string, captchaToken?: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
      ...(captchaToken ? { captchaToken } : {}),
    });
    return { error: error as Error | null };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    resetPostHog();
    Sentry.setUser(null);
    await supabase.auth.signOut();
  };

  const completeOnboarding = async () => {
    if (!effectiveUser) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('user_id', effectiveUser.id);

      if (error) throw error;
      setOnboardingCompleted(true);
      trackOnboardingCompleted({ user_id: effectiveUser.id });
    } catch (err) {
      console.error('Error completing onboarding:', err);
    }
  };

  return (
    <AuthContext.Provider value={{ 
        user, 
        session, 
        loading, 
        effectiveUser,
        signUp, 
        signIn, 
        signInWithDiscord, 
        signInWithGoogle,
        resetPassword, 
        updatePassword, 
        signOut,
        onboardingCompleted,
        onboardingLoading,
        completeOnboarding
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
